import { useEffect } from 'react';
import cx from 'clsx';
import { DateTime } from 'luxon';

import { Loading } from '@autopass-web/mochi-component';
import { MapPinFillIcon, RefreshIcon } from '@autopass-web/mochi-icons';

import { Bulletin, BulletinItem } from '../components/bulletin';
import { ModalLayout } from '../components/modal';
import { PageLayout } from '../components/UI';
import { environment } from '../environments/environment';
import { useParkingLotInfo } from '../routes/parking-lot';
import { analyticsService } from '../services/amplitude';
import storage from '../services/storage';
import { useSuccessPageBulletinBoards } from '../services/use-bulletin-boards';
import { useGetParkingRecords } from '../services/use-get-parking-records';
import { useLicensePlates } from '../services/use-license-plates';
import { useSearchParkingRecords } from '../services/use-query-parking-records';
import { useSendDownloadSms } from '../services/use-send-download-sms';
import { useUserMe } from '../services/use-user-me';
import { useDedupedCallback } from '../utils/use-dedupted-cb';

const ParkinLotInfo = ({
  parkingLotName,
  periodStr,
  pricingStr,
  startAtStr,
  refresh,
}: {
  parkingLotName: string;
  periodStr: string;
  pricingStr?: string;
  startAtStr?: string;
  refresh: () => void;
}) => {
  const currentStr = DateTime.now().toLocaleString(
    DateTime.DATETIME_SHORT_WITH_SECONDS,
  );

  return (
    <div className="bg-white rounded-xl p-6">
      {parkingLotName && (
        <>
          <div className="flex items-center gap-1">
            <MapPinFillIcon className="inline" />
            <span className="text-lg">{parkingLotName}</span>
          </div>
          <hr className="my-4" />
        </>
      )}
      <div className="grid grid-cols-[1fr,auto] gap-2 text-base">
        <div className="text-gray-400">進場時間</div>
        <div className="justify-self-end">{startAtStr}</div>
        <div className="text-gray-400">停車時數</div>
        <div className="justify-self-end">{periodStr}</div>
        {pricingStr && (
          <>
            <div className="text-gray-400">預估金額</div>
            <div className="justify-self-end text-[#00D7B2]">{pricingStr}</div>
          </>
        )}
      </div>
      <div className="pt-6 text-sm text-gray-400">
        <div className="flex items-center justify-center mb-1">
          更新時間 {currentStr}{' '}
          <RefreshIcon
            className="inline h-[16px] text-[#007AFF]"
            onClick={refresh}
          />
        </div>
        <div className="text-center  text-m">實際時間以出場扣款時間為主</div>
      </div>
    </div>
  );
};

export function Success() {
  const { userMe } = useUserMe({ enabled: false });
  const { licensePlates = [] } = useLicensePlates();
  const { parkingRecords = [], isPending, refetch } = useGetParkingRecords();
  const { sendDownloadSms } = useSendDownloadSms();
  const {
    parkingLotInfo,
    isPending: isParkingLotInfoPending,
    isValidParkingLot,
  } = useParkingLotInfo();

  const { bulletinBoards = [], isPending: isBulletinPending } =
    useSuccessPageBulletinBoards();

  const { isFirstTime } = storage.getAuth();

  const isReunited = !isFirstTime;

  const latestLicensePlate = licensePlates?.[0];

  const plateNumber = latestLicensePlate?.plate_number;

  const anal = useDedupedCallback(() => {
    if (!isValidParkingLot || (isValidParkingLot && !isParkingLotInfoPending)) {
      analyticsService.paymentSuccessPageview({
        isNewUser: isFirstTime,
        plateNumber,
        parkingLotName: parkingLotInfo?.name,
      });
    }
  }, [
    isReunited,
    parkingLotInfo?.name,
    plateNumber,
    isValidParkingLot,
    isParkingLotInfoPending,
  ]);

  useEffect(() => {
    !isReunited && sendDownloadSms();
  }, [isReunited, sendDownloadSms]);

  useEffect(anal, [anal]);

  const { searchedParkingRecords } = useSearchParkingRecords(
    {
      parkinglot_id: parkingLotInfo?.parkinglot?.id,
      vehicle_type: latestLicensePlate?.vehicle_type,
      plateNumber,
    },
    {
      enabled: !!latestLicensePlate && !!parkingLotInfo,
    },
  );

  const latestSearchedParkingRecord = searchedParkingRecords?.[0];

  const latestParkingRecord = parkingRecords.filter(
    ({ plate_number, parking_state }) =>
      plateNumber === plate_number && parking_state === 'parking',
  )?.[0];

  const latestRecord = latestParkingRecord ?? latestSearchedParkingRecord;

  const hasRecord = !!latestRecord;

  const titleText = (() => {
    if (!isReunited) {
      return `${plateNumber} 設定付款成功`;
    }

    return '重逢的麻吉，歡迎回來！';
  })();

  const title = (
    <h1
      className={cx(
        'text-[24px] text-center font-medium whitespace-pre mb-[20px] ',
      )}
    >
      {titleText}
    </h1>
  );

  const desc = (() => {
    if (hasRecord) {
      return (
        <div className="text-center text-[16px] mb-4">
          <p>你已是車麻吉會員，但還差最後一步...</p>
          <p>
            <a
              href={environment.DOWNLOAD_AUTOPASS_LINK}
              target="_blank"
              rel="noreferrer"
              className="text-[#007AFF]"
              onClick={() => {
                analyticsService.paymentSuccessLoginClick({
                  isNewUser: isFirstTime,
                  userId: userMe.id,
                  plateNumber,
                });
              }}
            >
              登入 APP
            </a>{' '}
            可看到更多的停車場與出行服務！
          </p>
        </div>
      );
    }

    return (
      <div className="text-center text-[16px]">
        <p>你已是車麻吉會員，但還差最後一步...</p>
      </div>
    );
  })();

  const availBoards = bulletinBoards.filter((board) => {
    const parkingLotId = parkingLotInfo?.parkinglot?.id;
    const parkingLotsShouldDisplay = (board.attributes.parkinglot_ids ?? '')
      .split(',')
      .map((text) => text.trim());

    if (!parkingLotId || !board.attributes.parkinglot_ids) {
      return true;
    }

    return parkingLotsShouldDisplay.includes(parkingLotId);
  });

  const bulletinComp =
    availBoards.length < 1 ? null : (
      <Bulletin isLoading={isBulletinPending || false}>
        {availBoards.map((board) => {
          const uri = (() => {
            switch (board.action.type) {
              case 'open_app':
                return environment.DOWNLOAD_AUTOPASS_LINK;

              case 'open_link':
                return board.action.open_link.uri;

              default:
                return '';
            }
          })();

          return (
            <BulletinItem
              {...{
                subTitle: board.attributes.sub_title,
                imageUrl: board.attributes.image_url,
                backgroundColor: board.attributes.background_color,
                title: board.attributes.title,
                uri,
                onClick: analyticsService.paymentSuccessAdClick,
              }}
            />
          );
        })}
      </Bulletin>
    );

  const children = (() => {
    if (isPending) {
      return <Loading />;
    }

    const startAtStr = (() => {
      const formatter = (datetime) => {
        const dtObject = datetime.toObject();

        return `${dtObject.year}年${dtObject.month}月${dtObject.day}日 ${dtObject.hour}:${dtObject.minute}:${dtObject.second}`;
      };

      if (latestParkingRecord) {
        return formatter(
          DateTime.fromSeconds(latestParkingRecord.checked_in_at),
        );
      }

      if (latestSearchedParkingRecord) {
        return formatter(
          DateTime.fromISO(latestSearchedParkingRecord.checked_in_at),
        );
      }

      return '';
    })();

    const pricingStr = (() => {
      if (latestParkingRecord) {
        const amount = Number(latestParkingRecord?.parking_amount ?? '');

        if (amount < 0) return '費率未知';
        return `$ ${amount}`;
      }

      return '';
    })();

    const periodStr = (() => {
      let parkingStart;

      if (latestParkingRecord) {
        parkingStart = latestParkingRecord.parking_date;
      } else if (latestSearchedParkingRecord) {
        parkingStart = latestSearchedParkingRecord.checked_in_at;
      }

      if (!parkingStart) return '';

      const parkingStartDiff =
        DateTime.fromISO(parkingStart).diffNow('minutes');

      const [h, m] = parkingStartDiff
        .toFormat('h:m')
        .split(':')
        .map((text) => {
          return Math.abs(Number(text.trim()));
        });

      let str = '';

      if (h > 0) {
        str += `${h} 小時`;
      }

      if (m > 0) {
        str += `${m} 分鐘`;
      }

      return str;
    })();

    return (
      <>
        {hasRecord && (
          <div>
            <ParkinLotInfo
              parkingLotName={
                latestParkingRecord?.parkinglot_name ??
                parkingLotInfo?.name ??
                ''
              }
              periodStr={periodStr}
              pricingStr={pricingStr}
              startAtStr={startAtStr}
              refresh={refetch}
            />
            <div className="text-center text-[14px] mb-4">
              <p>稍候免去繳費機，請直接出場</p>
              <p>明細將寄送至 {userMe.email}</p>
            </div>
          </div>
        )}
        {!hasRecord && (
          <div className="flex flex-col gap-5">
            <ModalLayout
              title="前往 App 查看車辨停車場"
              closeActionText="登入 App"
              onClose={() => {
                analyticsService.paymentSuccessLoginClick({
                  isNewUser: isFirstTime,
                  userId: userMe.id,
                  plateNumber,
                });
                window.location.href = environment.DOWNLOAD_AUTOPASS_LINK;
              }}
            >
              在支援車麻吉車牌辨識的停車場，都可以直接車辨出場。
            </ModalLayout>
            {bulletinComp}
          </div>
        )}
      </>
    );
  })();

  return <PageLayout {...{ title, children, desc }} />;
}
